import { registerTour } from "../tourRegistry";
import { getDragDropAnimationHTML } from "../animations/DragDropAnimation";

/**
 * 10-step onboarding tour.
 * Steps that navigate to new pages use beforeShowPromise with navigateAndWait.
 * Each navigation step includes meta.expectedPath for back-button detection.
 */
function getOnboardingSteps({ navigateAndWait }) {
  const totalSteps = 10;

  const stepCounter = (n) => `Step ${n} of ${totalSteps}`;

  return [
    // Step 1: Dashboard Welcome
    {
      id: 'onboarding-dashboard',
      title: `Welcome to T3 Books! <span class="t3-shepherd-step-count">${stepCounter(1)}</span>`,
      text: 'This is your dashboard — a real-time overview of your organization\'s finances. You\'ll see income vs. expense charts, cash balance trends, and key metrics at a glance.',
      attachTo: { element: '[data-tour="dashboard-welcome"]', on: 'bottom' },
      meta: { expectedPath: '/' },
      buttons: [
        {
          text: 'Skip Tour',
          classes: 't3-shepherd-skip-btn',
          action() { this.cancel(); },
        },
        {
          text: 'Next',
          classes: 't3-shepherd-next-btn',
          action() { this.next(); },
        },
      ],
    },

    // Step 2: Sidebar Navigation
    {
      id: 'onboarding-sidebar',
      title: `Navigation <span class="t3-shepherd-step-count">${stepCounter(2)}</span>`,
      text: 'Use the sidebar to navigate between the main sections: Foundation (accounts & funds setup), Upkeep (daily transactions), Reports, Contacts, and History.',
      attachTo: { element: '[data-tour="nav-sidebar"]', on: 'right' },
      meta: { expectedPath: '/' },
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Next',
          classes: 't3-shepherd-next-btn',
          action() { this.next(); },
        },
      ],
    },

    // Step 3: Chart of Accounts (navigates)
    {
      id: 'onboarding-chart-of-accounts',
      title: `Chart of Accounts <span class="t3-shepherd-step-count">${stepCounter(3)}</span>`,
      text: 'Your chart of accounts organizes all financial accounts by type — Assets, Liabilities, Income, and Expenses. This is the foundation of your bookkeeping.',
      attachTo: { element: '[data-tour="coa-page"]', on: 'bottom' },
      meta: { expectedPath: '/foundation/chart-of-accounts' },
      beforeShowPromise() {
        return navigateAndWait('/foundation/chart-of-accounts', '[data-tour="coa-page"]').catch(() => {
          // Navigation failed — step will show centered without target
        });
      },
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Next',
          classes: 't3-shepherd-next-btn',
          action() { this.next(); },
        },
      ],
    },

    // Step 4: Rearrange (same page)
    {
      id: 'onboarding-rearrange',
      title: `Rearrange Accounts <span class="t3-shepherd-step-count">${stepCounter(4)}</span>`,
      text: `Click the drag handle on any account row to rearrange with drag and drop. You can also add new accounts, rename groups, and enable or disable individual accounts.${getDragDropAnimationHTML()}`,
      attachTo: { element: '[data-tour="coa-account-drag-handle"]', on: 'right' },
      meta: { expectedPath: '/foundation/chart-of-accounts' },
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Next',
          classes: 't3-shepherd-next-btn',
          action() { this.next(); },
        },
      ],
    },

    // Step 5: Funds (navigates)
    {
      id: 'onboarding-funds',
      title: `Funds <span class="t3-shepherd-step-count">${stepCounter(5)}</span>`,
      text: 'Funds track restricted and unrestricted money separately — essential for nonprofit accounting. Each transaction can be assigned to a specific fund.',
      attachTo: { element: '[data-tour="funds-page"]', on: 'bottom' },
      meta: { expectedPath: '/foundation/funds-list' },
      beforeShowPromise() {
        return navigateAndWait('/foundation/funds-list', '[data-tour="funds-page"]').catch(() => {
          // Navigation failed — step will show centered without target
        });
      },
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Next',
          classes: 't3-shepherd-next-btn',
          action() { this.next(); },
        },
      ],
    },

    // Step 6: Bank Accounts (navigates)
    {
      id: 'onboarding-bank-accounts',
      title: `Bank Accounts <span class="t3-shepherd-step-count">${stepCounter(6)}</span>`,
      text: 'This is your register — where you enter and manage bank transactions. You can also import transactions from CSV files or connect directly to your bank via Plaid.',
      attachTo: { element: '[data-tour="banking-page"]', on: 'bottom' },
      meta: { expectedPath: '/upkeep/bank-accounts' },
      beforeShowPromise() {
        return navigateAndWait('/upkeep/bank-accounts', '[data-tour="banking-page"]').catch(() => {
          // Navigation failed — step will show centered without target
        });
      },
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Next',
          classes: 't3-shepherd-next-btn',
          action() { this.next(); },
        },
      ],
    },

    // Step 7: Account Selector (same page)
    {
      id: 'onboarding-account-selector',
      title: `Account Selector <span class="t3-shepherd-step-count">${stepCounter(7)}</span>`,
      text: 'Switch between your bank accounts here. Each account has its own register with running balances.',
      attachTo: { element: '[data-tour="banking-account-selector"]', on: 'bottom' },
      meta: { expectedPath: '/upkeep/bank-accounts' },
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Next',
          classes: 't3-shepherd-next-btn',
          action() { this.next(); },
        },
      ],
    },

    // Step 8: Reports (navigates)
    {
      id: 'onboarding-reports',
      title: `Reports <span class="t3-shepherd-step-count">${stepCounter(8)}</span>`,
      text: 'Real-time financial reports powered by BigQuery. Generate balance sheets, income statements, and more — all reflecting your latest transaction data instantly.',
      attachTo: { element: '[data-tour="reports-page"]', on: 'bottom' },
      meta: { expectedPath: '/reports/balance-sheet' },
      beforeShowPromise() {
        return navigateAndWait('/reports/balance-sheet', '[data-tour="reports-page"]').catch(() => {
          // Navigation failed — step will show centered without target
        });
      },
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Next',
          classes: 't3-shepherd-next-btn',
          action() { this.next(); },
        },
      ],
    },

    // Step 9: Top Nav Pills (same page)
    {
      id: 'onboarding-top-nav',
      title: `Section Navigation <span class="t3-shepherd-step-count">${stepCounter(9)}</span>`,
      text: 'Use these pills to quickly switch between report types — Balance Sheet, Income Statement, General Ledger, and more.',
      attachTo: { element: '[data-tour="nav-top-pills"]', on: 'bottom' },
      meta: { expectedPath: '/reports/balance-sheet' },
      buttons: [
        {
          text: 'Back',
          classes: 't3-shepherd-back-btn',
          action() { this.back(); },
        },
        {
          text: 'Next',
          classes: 't3-shepherd-next-btn',
          action() { this.next(); },
        },
      ],
    },

    // Step 10: Complete (centered, no target)
    {
      id: 'onboarding-complete',
      title: `You're all set! <span class="t3-shepherd-step-count">${stepCounter(10)}</span>`,
      text: 'You now know the essentials of T3 Books. Look for the help (?) button in the top navigation for page-specific tours anytime. Happy bookkeeping!',
      buttons: [
        {
          text: 'Finish Tour',
          classes: 't3-shepherd-next-btn',
          action() { this.complete(); },
        },
      ],
    },
  ];
}

registerTour({
  id: 'onboarding',
  pathPattern: '/', // starts on dashboard
  getSteps: getOnboardingSteps,
  label: 'Onboarding Tour',
});
